import { Pipe, PipeTransform } from '@angular/core';
import { DateTime } from 'luxon';

/**
 * Converts a date and timezone to a DST-aware timezone offset string.
 * This ensures times are displayed correctly regardless of DST transitions.
 *
 * Usage: {{ dateValue | timezoneOffset:timezoneName | date:'shortTime' }}
 *
 * @example
 * // For a date on March 15, 2024 in America/New_York (EST)
 * {{ '2024-03-15T07:41:00' | timezoneOffset:'America/New_York' }}
 * // Returns: '-05:00'
 *
 * // For a date on July 15, 2024 in America/New_York (EDT)
 * {{ '2024-07-15T07:41:00' | timezoneOffset:'America/New_York' }}
 * // Returns: '-04:00'
 */
@Pipe({ name: 'timezoneOffset' })
export class TimezoneOffsetPipe implements PipeTransform {
  transform(value: string | Date | DateTime, timezone: string): string {
    if (!value || !timezone) { return null; }

    let dt: DateTime;

    if (value instanceof DateTime) {
      dt = value.setZone(timezone);
    } else if (value instanceof Date) {
      // Date objects don't have timezone info, treat as UTC and convert
      dt = DateTime.fromJSDate(value, { zone: 'UTC' }).setZone(timezone);
    } else if (typeof value === 'string') {
      dt = DateTime.fromISO(value, { zone: timezone });
    } else {
      return null;
    }

    // Return offset in format like '+05:00' or '-04:00'
    return dt.toFormat('ZZZ');
  }
}
